import React from 'react';
import styled from 'styled-components';
import LoadingIcon from '../../assets/svg/loader.svg';
import SearchIconSvg from '../../assets/svg/search.svg';

interface TypeaheadOption {
  id: string;
  name: string;
}

interface TypeaheadProps {
  name: string;
  options: TypeaheadOption[];
  selectedOption?: TypeaheadOption;
  value: string;
  onChange: (value: string, option?: TypeaheadOption) => void;
  onSearch: (value: string) => void;
  emptyValue: string;
  disabled?: boolean;
  isLoading?: boolean;
  onCreateOption?: (name: string) => void;
  renderCreateOption?: (name: string) => React.ReactNode;
  placeholder?: string;
}

const TypeaheadContainer = styled.div`
  position: relative;
`;

const TypeaheadInput = styled.input`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  box-sizing: border-box;
  box-shadow: inset 0 1px 5px 0 rgba(0, 0, 0, 0.15);
  color: ${(props) => props.theme.colors.body};
  display: block;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 1em;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  height: 36px;
  letter-spacing: 0.1px;
  line-height: normal;
  outline: none;
  padding: 0 40px 0 16px;
  width: 100%;

  &:focus {
    border: solid 1px ${(props) => props.theme.colors.body};
    box-shadow: inset 0 1px 5px 0 rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: rgba(244, 244, 244, 0.6);
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const LoadingIndicator = styled.img`
  height: 18px;
  position: absolute;
  right: 14px;
  top: 9px;
  width: 18px;
`;

const SearchIcon = styled.img`
  height: 18px;
  pointer-events: none;
  position: absolute;
  right: 14px;
  top: 9px;
  width: 18px;
`;

const TypeaheadOptions = styled.ul`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.disabled};
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  left: 0;
  list-style: none;
  margin: 4px 0 0;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px 0;
  position: absolute;
  right: 0;
  z-index: 10;
`;

const TypeaheadOptionItem = styled.li<{ selected?: boolean }>`
  color: ${(props) => (props.selected ? '#fff' : props.theme.colors.body)};
  cursor: pointer;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  padding: 8px 16px;
  background: ${(props) => (props.selected ? props.theme.colors.primaryBrand : '#fff')};
`;

const BoldMatch = styled.span`
  font-weight: bold;
`;

const EmptyOption = styled.li`
  color: ${(props) => props.theme.colors.disabled};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  padding: 8px 16px;
`;

const BLUR_CLOSE_DELAY = 100;
const SELECT_INPUT_DELAY = 0;
const BEGIN_TYPING_MESSAGE = 'Begin typing to search';
const NO_RESULTS_MESSAGE = 'No results found';

const normalizeSearchTerm = (value: string) => value.trim().toLowerCase();

const getMatchRange = (name: string, searchTerm: string) => {
  const normalizedSearchTerm = normalizeSearchTerm(searchTerm);
  const matchIndex = name.toLowerCase().indexOf(normalizedSearchTerm);

  if (!normalizedSearchTerm || matchIndex === -1) return undefined;

  return {
    end: matchIndex + normalizedSearchTerm.length,
    start: matchIndex,
  };
};

const renderOptionName = (name: string, searchTerm: string) => {
  const matchRange = getMatchRange(name, searchTerm);

  if (!matchRange) return name;

  const prefix = name.slice(0, matchRange.start);
  const match = name.slice(matchRange.start, matchRange.end);
  const suffix = name.slice(matchRange.end);

  return (
    <>
      {prefix}
      {match.split('').map((character, characterIndex, characters) => (
        <BoldMatch key={`${prefix}${characters.slice(0, characterIndex + 1).join('')}`}>{character}</BoldMatch>
      ))}
      {suffix}
    </>
  );
};

const getSelectedOptionName = (selectedOption: TypeaheadOption | undefined, value: string, emptyValue: string, inputValue: string) => {
  if (selectedOption && selectedOption.id === value) return selectedOption.name;
  if (value === emptyValue) return '';

  return inputValue;
};

const getEmptyOptionMessage = (inputValue: string, options: TypeaheadOption[]) => {
  if (!inputValue.trim()) return BEGIN_TYPING_MESSAGE;
  if (!options.length) return NO_RESULTS_MESSAGE;

  return undefined;
};

interface TypeaheadDropdownProps {
  activeOptionIndex: number;
  canCreateOption: boolean;
  inputValue: string;
  onCreateOption: () => void;
  onHoverOption: (index: number) => void;
  onSelectOption: (option: TypeaheadOption) => void;
  options: TypeaheadOption[];
  renderCreateOption?: (name: string) => React.ReactNode;
}

const TypeaheadDropdown: React.FC<TypeaheadDropdownProps> = ({
  activeOptionIndex,
  canCreateOption,
  inputValue,
  onCreateOption,
  onHoverOption,
  onSelectOption,
  options,
  renderCreateOption,
}) => {
  const createOptionIndex = options.length;
  const emptyMessage = getEmptyOptionMessage(inputValue, options);
  const createOptionName = inputValue.trim();

  return (
    <TypeaheadOptions>
      {options.map((option, index) => (
        <TypeaheadOptionItem
          key={option.id}
          onMouseDown={(event: React.MouseEvent) => {
            event.preventDefault();
            onSelectOption(option);
          }}
          onMouseOver={() => onHoverOption(index)}
          selected={index === activeOptionIndex}
        >
          {renderOptionName(option.name, inputValue)}
        </TypeaheadOptionItem>
      ))}
      {canCreateOption ? (
        <TypeaheadOptionItem
          onMouseDown={(event: React.MouseEvent) => {
            event.preventDefault();
            onCreateOption();
          }}
          onMouseOver={() => onHoverOption(createOptionIndex)}
          selected={activeOptionIndex === createOptionIndex}
        >
          {renderCreateOption ? renderCreateOption(createOptionName) : createOptionName}
        </TypeaheadOptionItem>
      ) : emptyMessage ? (
        <EmptyOption>{emptyMessage}</EmptyOption>
      ) : null}
    </TypeaheadOptions>
  );
};

const Typeahead: React.FC<TypeaheadProps> = ({
  name,
  options,
  selectedOption,
  value,
  onChange,
  onSearch,
  emptyValue,
  disabled,
  isLoading,
  onCreateOption,
  renderCreateOption,
  placeholder,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = React.useState(0);
  const [pendingSearchTerm, setPendingSearchTerm] = React.useState('');
  const [settledSearchTerm, setSettledSearchTerm] = React.useState('');
  const selectedOptionName = getSelectedOptionName(selectedOption, value, emptyValue, inputValue);
  const trimmedInputValue = inputValue.trim();
  const normalizedInputValue = normalizeSearchTerm(inputValue);
  const displayValue = isOpen ? inputValue : selectedOptionName;
  const displayPlaceholder = value === emptyValue ? placeholder : undefined;
  const searchOptions = trimmedInputValue ? options : [];
  const hasExactMatch = searchOptions.some((option) => normalizeSearchTerm(option.name) === normalizedInputValue);
  const canCreateOption = Boolean(
    onCreateOption &&
      trimmedInputValue &&
      normalizedInputValue === normalizeSearchTerm(settledSearchTerm) &&
      !isLoading &&
      !hasExactMatch
  );
  const optionCount = searchOptions.length + (canCreateOption ? 1 : 0);

  React.useEffect(() => {
    if (!isLoading && pendingSearchTerm.trim()) {
      setSettledSearchTerm(pendingSearchTerm);
    }
  }, [isLoading, pendingSearchTerm]);

  const openTypeahead = () => {
    if (disabled) return;

    if (!isOpen) {
      setInputValue(selectedOptionName);
      setPendingSearchTerm('');
      setSettledSearchTerm('');
      setActiveOptionIndex(0);
      window.setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, SELECT_INPUT_DELAY);
    }

    setIsOpen(true);
  };

  const selectOption = (option: TypeaheadOption) => {
    setInputValue(option.name);
    setIsOpen(false);
    onChange(option.id, option);
  };

  const createOption = () => {
    const nextOptionName = trimmedInputValue;

    if (!nextOptionName || !onCreateOption) return;

    setInputValue(nextOptionName);
    setIsOpen(false);
    onCreateOption(nextOptionName);
  };

  const handleInputChange = (nextValue: string) => {
    if (disabled) return;

    const normalizedNextValue = normalizeSearchTerm(nextValue);
    const matchingOption = options.find((option) => normalizeSearchTerm(option.name) === normalizedNextValue);

    setInputValue(nextValue);
    setIsOpen(true);
    setActiveOptionIndex(0);
    setPendingSearchTerm(nextValue);
    setSettledSearchTerm('');
    onSearch(nextValue);

    if (!normalizedNextValue) {
      onChange(emptyValue);
      return;
    }

    if (matchingOption) {
      onChange(matchingOption.id, matchingOption);
    }
  };

  const handleBlur = () => {
    if (disabled) return;

    window.setTimeout(() => {
      setInputValue(selectedOptionName);
      setIsOpen(false);
    }, BLUR_CLOSE_DELAY);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        setActiveOptionIndex((index) => Math.min(index + 1, Math.max(optionCount - 1, 0)));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveOptionIndex((index) => Math.max(index - 1, 0));
        break;
      case 'Enter':
        if (!isOpen) return;

        event.preventDefault();

        if (searchOptions[activeOptionIndex]) {
          selectOption(searchOptions[activeOptionIndex]);
        } else if (canCreateOption && activeOptionIndex === searchOptions.length) {
          createOption();
        }

        break;
      case 'Escape':
        setIsOpen(false);
        setInputValue(selectedOptionName);
        break;
      default:
        break;
    }
  };

  return (
    <TypeaheadContainer>
      <TypeaheadInput
        autoComplete="off"
        ref={inputRef}
        name={name}
        disabled={disabled}
        onBlur={handleBlur}
        onChange={(event) => handleInputChange(event.currentTarget.value)}
        onClick={openTypeahead}
        onFocus={openTypeahead}
        onKeyDown={handleKeyDown}
        placeholder={displayPlaceholder}
        value={displayValue}
      />
      {isLoading ? <LoadingIndicator src={LoadingIcon} alt="Loading" /> : <SearchIcon src={SearchIconSvg} alt="" />}
      {isOpen && (
        <TypeaheadDropdown
          activeOptionIndex={activeOptionIndex}
          canCreateOption={canCreateOption}
          inputValue={inputValue}
          onCreateOption={createOption}
          onHoverOption={setActiveOptionIndex}
          onSelectOption={selectOption}
          options={searchOptions}
          renderCreateOption={renderCreateOption}
        />
      )}
    </TypeaheadContainer>
  );
};

export default Typeahead;
